import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { TextInput } from '../../../components/TextInput';
import { TablePagination } from '../../../components/TablePagination';
import { ApiError } from '../../../services/api';
import { getBillingOrganizations } from '../../../services/admin-billing';
import type { BillingOrganizationListItem } from '../../../types/adminBilling';
import {
  billingModeLabel,
  dateOnlyCell,
  daysOverdueText,
  formatMoney,
  instantCell,
  planCellText,
} from './billingFormat';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import './Billing.css';

const PAGE_SIZE = 20;

const parsePage = (raw: string | null): number => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/**
 * Every organization's billing, worst first.
 *
 * **The order is the server's.** Overdue before everything else, then by what is due soonest. The
 * client does not re-sort and does not re-derive "overdue" from a due date — see
 * `BillingInvoiceDisplayStatus`.
 *
 * The column that earns its place here is the last one: an organization with **no deliverable
 * contact** can never be reminded of anything, so its balance will sit there until a person
 * notices. That is a different problem from an overdue balance and it is called out twice, once as
 * a count above the table and once per row, rather than being left to an empty cell.
 */
export const BillingSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initial] = useState(() => ({
    page: parsePage(searchParams.get('page')),
    search: searchParams.get('search') ?? '',
    overdueOnly: searchParams.get('overdueOnly') === 'true',
  }));

  const [pageNumber, setPageNumber] = useState(initial.page);
  const [searchTerm, setSearchTerm] = useState(initial.search);
  const [overdueOnly, setOverdueOnly] = useState(initial.overdueOnly);

  const [items, setItems] = useState<BillingOrganizationListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalOutstandingCents, setTotalOutstandingCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const filtersLabelId = useId();

  /*
    Typing in the search box fires a request per keystroke, and they do not necessarily come back in
    order. The token is what stops an earlier, slower response painting over a later one: the abort
    covers the common case, but a response already in flight when the signal fires still resolves.
  */
  const requestToken = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const token = requestToken.current + 1;
    requestToken.current = token;

    setLoading(true);
    setError(null);

    getBillingOrganizations(
      {
        page: pageNumber,
        pageSize: PAGE_SIZE,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(overdueOnly ? { overdueOnly: true } : {}),
      },
      controller.signal
    )
      .then((response) => {
        if (requestToken.current !== token) return;
        setItems(response.items);
        setTotalCount(response.totalCount);
        setTotalOutstandingCents(response.totalOutstandingCents);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || requestToken.current !== token) return;
        setError(describeError(err, 'Could not load billing. Please try again.'));
        setLoading(false);
      });

    return () => controller.abort();
  }, [pageNumber, searchTerm, overdueOnly, reloadToken]);

  // The filters live in the URL so a paged, filtered view is linkable and browser back steps
  // through it. `replace` keeps one history entry per visit rather than one per keystroke.
  useEffect(() => {
    const params = new URLSearchParams();
    if (pageNumber > 1) params.set('page', String(pageNumber));
    if (searchTerm) params.set('search', searchTerm);
    if (overdueOnly) params.set('overdueOnly', 'true');
    setSearchParams(params, { replace: true });
  }, [pageNumber, searchTerm, overdueOnly, setSearchParams]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPageNumber(1);
  }, []);

  const unreachableCount = items.filter((item) => item.hasNoDeliverableContact).length;

  return (
    <AdminPage labelledBy="billing-title">
      <AdminPageHeader
        title="Billing"
        titleId="billing-title"
        subtitle="What every organization owes, worst first. Open one to change its plan, its accounts payable contacts or its invoices."
        actions={
          <>
            <p className="billing__total" data-testid="billing-total-outstanding">
              <span className="billing__total-label">Outstanding</span>
              <span className="billing__total-value">{formatMoney(totalOutstandingCents)}</span>
            </p>
            <Link className="billing__settings-link" to="/admin/billing/settings">
              Billing settings
            </Link>
          </>
        }
      />

      <div className="billing__filters" role="group" aria-labelledby={filtersLabelId}>
        <span className="billing__filters-label" id={filtersLabelId}>
          Filter organizations
        </span>
        <TextInput
          label="Search"
          type="search"
          placeholder="Organization name"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          data-testid="billing-search"
        />
        <Checkbox
          label="Overdue only"
          checked={overdueOnly}
          onChange={(e) => {
            setOverdueOnly(e.target.checked);
            setPageNumber(1);
          }}
          data-testid="billing-overdue-only"
        />
      </div>

      {error && (
        <div className="admin-error" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setReloadToken((token) => token + 1)}
            data-testid="billing-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {/*
        Always mounted, so the count is announced when it appears. A live region inserted along
        with its text is not reliably read out.
      */}
      <div role="status" className="billing__status">
        {!loading && unreachableCount > 0 && (
          <p className="billing__banner billing__banner--warning" data-testid="billing-unreachable">
            {unreachableCount === 1
              ? '1 organization on this page has no billing contact, so it cannot be reminded.'
              : `${unreachableCount} organizations on this page have no billing contact, so they cannot be reminded.`}
          </p>
        )}
      </div>

      {loading ? (
        <p className="admin-state">Loading billing...</p>
      ) : (
        <>
          <div className="billing__table-container">
            <table className="billing__table">
              <thead>
                <tr>
                  <th scope="col">Organization</th>
                  <th scope="col">Plan</th>
                  <th scope="col">Next invoice</th>
                  <th scope="col">Outstanding</th>
                  <th scope="col">Overdue</th>
                  <th scope="col">Last reminder</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="billing__empty" data-testid="billing-empty">
                      {searchTerm || overdueOnly
                        ? 'No organizations match these filters.'
                        : 'No organizations yet.'}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.organizationId}
                      className={`billing__row${
                        item.hasNoDeliverableContact ? ' billing__row--unreachable' : ''
                      }`}
                      data-testid={`billing-row-${item.organizationId}`}
                    >
                      <td className="billing__cell-name">
                        <Link
                          to={`/admin/billing/organizations/${item.organizationId}`}
                          className="billing__row-link"
                        >
                          {item.organizationName}
                        </Link>
                        {/*
                          Which system bills them, on the row. Everything else in it reads
                          differently depending on the answer: a Stripe-billed organization has no
                          next invoice date of ours, is chased by Stripe rather than by the
                          reminder schedule, and its invoices cannot be settled from here.
                        */}
                        {item.isStripeManaged && (
                          <span
                            className="billing__flag billing__flag--stripe"
                            data-testid={`billing-stripe-managed-${item.organizationId}`}
                          >
                            {billingModeLabel(true)}
                          </span>
                        )}
                        {/*
                          Already paused. On the overdue list this is the difference between an
                          account nobody has looked at and one that has already been dealt with.
                        */}
                        {item.isPaused && (
                          <span
                            className="billing__flag billing__flag--paused"
                            data-testid={`billing-paused-${item.organizationId}`}
                          >
                            Paused
                          </span>
                        )}
                        {item.hasNoDeliverableContact && (
                          <span
                            className="billing__flag"
                            data-testid={`billing-no-contact-${item.organizationId}`}
                          >
                            No billing contact
                          </span>
                        )}
                        {item.hasNoBillingSource && (
                          <span
                            className="billing__flag"
                            data-testid={`billing-no-source-${item.organizationId}`}
                            title="Stripe holds this organization but is not raising a recurring invoice for it, so nobody is billed."
                          >
                            Nothing is billing this
                          </span>
                        )}
                      </td>
                      <td>{planCellText(item.amountCents, item.currency, item.interval)}</td>
                      <td>{dateOnlyCell(item.nextInvoiceOn)}</td>
                      <td>{formatMoney(item.outstandingCents, item.currency)}</td>
                      <td>
                        {item.overdueInvoiceCount > 0 ? (
                          <span className="billing__overdue">
                            {item.overdueInvoiceCount} overdue
                            {/*
                              How long it has been owed, which is the thing that decides what to do
                              about it. The server derives the days, so this never disagrees with
                              the detail screen; the date is the fallback for a server too old to
                              send them, and better than nothing at all.
                            */}
                            <span className="billing__overdue-since">
                              {item.oldestInvoiceDaysOverdue > 0
                                ? daysOverdueText(item.oldestInvoiceDaysOverdue)
                                : item.oldestDueOn
                                  ? `since ${dateOnlyCell(item.oldestDueOn)}`
                                  : null}
                            </span>
                          </span>
                        ) : (
                          'None'
                        )}
                      </td>
                      <td>{instantCell(item.lastReminderAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={pageNumber}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setPageNumber}
          />
        </>
      )}
    </AdminPage>
  );
};
